import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Users, CheckCircle, AlertCircle, LogIn } from 'lucide-react';
import { teamService } from '@/services/team.service';
import { useAuthStore } from '@/store/authStore';
import { cn } from '@/utils/cn';

export const AcceptInvitePage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState<'pending' | 'accepted' | 'declined' | 'error'>('pending');
  const [errorMsg, setErrorMsg] = useState('');

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 relative overflow-hidden bg-[#FFFDF7]">
        <div className="w-full max-w-[420px] bg-white rounded-xl border-2 border-[#1a1a2e] shadow-[4px_4px_0px_#1a1a2e] p-8 text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Invalid invitation</h2>
          <p className="text-sm text-gray-500 mb-6">This invitation link is invalid.</p>
          <Link to="/login" className="text-sm text-blue-600 hover:text-blue-700 font-medium">
            Go to sign in
          </Link>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 relative overflow-hidden bg-[#FFFDF7]">
        <div className="w-full max-w-[420px]">
          <div className="flex items-center justify-center -space-x-1 mb-8">
            <img src="/logo-icon.svg" alt="ChatCup" className="h-12" />
            <img src="/logo-name.svg" alt="ChatCup.ai" className="h-7" />
          </div>
          <div className="bg-white rounded-xl border-2 border-[#1a1a2e] shadow-[4px_4px_0px_#1a1a2e] p-8 text-center">
            <Users className="w-12 h-12 text-blue-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">Team invitation</h2>
            <p className="text-sm text-gray-500 mb-6">
              You've been invited to join a team on ChatCup. Please sign in or create an account to accept.
            </p>
            <div className="flex flex-col gap-3">
              <Link
                to={`/login?redirect=/invite/${token}`}
                className={cn(
                  'inline-flex items-center justify-center gap-2 h-11 px-6',
                  'bg-blue-500 text-white font-bold rounded-xl border-2 border-[#1a1a2e]',
                  'shadow-[3px_3px_0px_#1a1a2e] hover:shadow-[5px_5px_0px_#1a1a2e]',
                  'hover:translate-x-[-2px] hover:translate-y-[-2px] transition-all duration-200'
                )}
              >
                <LogIn className="w-4 h-4" />
                Sign in
              </Link>
              <Link
                to={`/register?redirect=/invite/${token}`}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                Create an account
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const handleAccept = async () => {
    setIsLoading(true);
    try {
      await teamService.acceptInvitation(token);
      setStatus('accepted');
      toast.success('Invitation accepted!');
    } catch (err: any) {
      const msg = err?.response?.data?.error?.message || err?.message || 'Failed to accept invitation';
      setErrorMsg(msg);
      setStatus('error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDecline = async () => {
    setIsLoading(true);
    try {
      await teamService.declineInvitation(token);
      setStatus('declined');
    } catch (err: any) {
      const msg = err?.response?.data?.error?.message || err?.message || 'Failed to decline invitation';
      setErrorMsg(msg);
      setStatus('error');
    } finally {
      setIsLoading(false);
    }
  };

  if (status === 'accepted') {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 relative overflow-hidden bg-[#FFFDF7]">
        <div className="w-full max-w-[420px] bg-white rounded-xl border-2 border-[#1a1a2e] shadow-[4px_4px_0px_#1a1a2e] p-8 text-center">
          <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Welcome to the team!</h2>
          <p className="text-sm text-gray-500 mb-6">
            You've successfully joined the organization.
          </p>
          <button
            onClick={() => navigate('/')}
            className={cn(
              'inline-flex items-center justify-center h-11 px-8',
              'bg-blue-500 text-white font-bold rounded-xl border-2 border-[#1a1a2e]',
              'shadow-[3px_3px_0px_#1a1a2e] hover:shadow-[5px_5px_0px_#1a1a2e]',
              'hover:translate-x-[-2px] hover:translate-y-[-2px] transition-all duration-200'
            )}
          >
            Go to dashboard
          </button>
        </div>
      </div>
    );
  }

  if (status === 'declined') {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 relative overflow-hidden bg-[#FFFDF7]">
        <div className="w-full max-w-[420px] bg-white rounded-xl border-2 border-[#1a1a2e] shadow-[4px_4px_0px_#1a1a2e] p-8 text-center">
          <h2 className="text-xl font-bold text-gray-900 mb-2">Invitation declined</h2>
          <p className="text-sm text-gray-500 mb-6">You've declined this invitation.</p>
          <Link to="/" className="text-sm text-blue-600 hover:text-blue-700 font-medium">
            Go to dashboard
          </Link>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 relative overflow-hidden bg-[#FFFDF7]">
        <div className="w-full max-w-[420px] bg-white rounded-xl border-2 border-[#1a1a2e] shadow-[4px_4px_0px_#1a1a2e] p-8 text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Something went wrong</h2>
          <p className="text-sm text-gray-500 mb-6">{errorMsg}</p>
          <Link to="/" className="text-sm text-blue-600 hover:text-blue-700 font-medium">
            Go to dashboard
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 relative overflow-hidden bg-[#FFFDF7]">
      <div className="w-full max-w-[420px]">
        <div className="flex items-center justify-center -space-x-1 mb-8">
          <img src="/logo-icon.svg" alt="ChatCup" className="h-12" />
          <img src="/logo-name.svg" alt="ChatCup.ai" className="h-7" />
        </div>
        <div className="bg-white rounded-xl border-2 border-[#1a1a2e] shadow-[4px_4px_0px_#1a1a2e] p-8 text-center">
          <Users className="w-12 h-12 text-blue-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Team invitation</h2>
          <p className="text-sm text-gray-500 mb-6">
            You've been invited to join a team on ChatCup.
          </p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={handleAccept}
              disabled={isLoading}
              className={cn(
                'h-11 px-8 flex items-center justify-center',
                'bg-blue-500 text-white font-bold rounded-xl border-2 border-[#1a1a2e]',
                'shadow-[3px_3px_0px_#1a1a2e] hover:shadow-[5px_5px_0px_#1a1a2e]',
                'hover:translate-x-[-2px] hover:translate-y-[-2px] transition-all duration-200',
                'disabled:opacity-50 disabled:cursor-not-allowed'
              )}
            >
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                'Accept'
              )}
            </button>
            <button
              onClick={handleDecline}
              disabled={isLoading}
              className={cn(
                'h-11 px-8 flex items-center justify-center',
                'bg-white text-gray-700 font-bold rounded-xl border-2 border-[#1a1a2e]',
                'shadow-[3px_3px_0px_#1a1a2e] hover:shadow-[5px_5px_0px_#1a1a2e]',
                'hover:translate-x-[-2px] hover:translate-y-[-2px] transition-all duration-200',
                'disabled:opacity-50 disabled:cursor-not-allowed'
              )}
            >
              Decline
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
